// The report document model: turns the evaluated canvases into plain data (strings only) for the PDF renderer.
// Pure JavaScript, no DOM, no jsPDF. Never throws on blank or half-filled canvases.
//
//   buildDoc({ states, agg, learner, generatedOn }) -> model (see the shape at the end of this file)
//
// states and agg come from evaluateAll(entries, DEFS). learner is { name, email, organisation }.
import { tableRows } from './engine.js';
import { key, listOf } from './helpers.js';
import { ARC_FAMILY } from './spine.js';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const TAG_LETTERS = ['M', 'E', 'G'];

// ---------------------------------------------------------------- small helpers
const str = (x) => (x === null || x === undefined ? '' : String(x).trim());
const isObj = (x) => x !== null && typeof x === 'object';

// '2026-10-05T09:30:00Z' -> '5 October 2026' ('' when the text is not a date)
export function formatDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(str(iso));
  if (!m) return '';
  const month = MONTHS[Number(m[2]) - 1];
  return month ? `${Number(m[3])} ${month} ${m[1]}` : '';
}

// the label of an option, for a stored value. Options are plain strings or { v, label, hint }.
function optionLabel(options, raw) {
  const v = str(raw);
  if (!v) return '';
  for (const o of Array.isArray(options) ? options : []) {
    if (isObj(o)) {
      if (String(o.v) === v) return str(o.label !== undefined ? o.label : o.v);
    } else if (String(o) === v) return String(o);
  }
  return v;
}

// a typed number with its unit or prefix: '30' + '%' -> '30%'; '5' + 'points' -> '5 points'; 240000 -> '240,000'
const groupDigits = (s) => (/^\d{5,}(\.\d+)?$/.test(s) ? s.replace(/^\d+/, (m) => m.replace(/\B(?=(\d{3})+(?!\d))/g, ',')) : s);

function numberText(field, raw) {
  const v = groupDigits(str(raw));
  if (!v) return '';
  const unit = str(field.unit);
  const prefix = str(field.prefix);
  const body = prefix && !v.startsWith(prefix) ? `${prefix}${v}` : v;
  if (!unit || body.endsWith(unit)) return body;
  return unit === '%' ? `${body}%` : `${body} ${unit}`;
}

// one displayed value for any field kind: { value, tag, calc }
function valueOf(field, raw, calcMap, k) {
  const out = { value: '', tag: '', calc: false };
  switch (field.kind) {
    case 'calc':
      out.value = str(calcMap[k]);
      out.calc = true;
      break;
    case 'tag':
      out.tag = TAG_LETTERS.includes(str(raw).toUpperCase()) ? str(raw).toUpperCase() : '';
      break;
    case 'choice':
      out.value = optionLabel(field.options, raw);
      break;
    case 'multi':
      out.value = listOf(raw)
        .map((x) => optionLabel(field.options, x))
        .join(', ');
      break;
    case 'number':
      out.value = numberText(field, raw);
      break;
    default:
      out.value = str(raw);
  }
  return out;
}

// ---------------------------------------------------------------- blocks
function kvBlock(block, ctx) {
  const rows = (block.fields || []).map((f) => {
    const cell = valueOf(f, ctx.v[f.id], ctx.calc, f.id);
    return { label: str(f.label), value: cell.value, tag: cell.tag, calc: cell.calc };
  });
  return { kind: 'kv', rows };
}

// a user-managed row counts when it has at least one typed (non-calc) cell
const hasTyped = (block, row, v) =>
  block.columns.some((c) => c.kind !== 'calc' && str(v[key(block.id, row.id, c.id)]) !== '');

function tableBlock(block, ctx) {
  const columns = (block.columns || []).map((c) => ({ label: str(c.label), w: Number(c.w) > 0 ? Number(c.w) : 2 }));
  let list = [];
  try {
    list = tableRows(block, ctx.v, ctx.up) || [];
  } catch (e) {
    list = [];
  }
  const managed = !(block.rows && (block.rows.fixed || block.rows.from));
  const rows = list
    .filter((row) => !managed || hasTyped(block, row, ctx.v))
    .map((row) => ({
      label: str(row.label),
      sub: str(row.sub),
      cells: block.columns.map((c) => {
        const k = key(block.id, row.id, c.id);
        return valueOf(c, ctx.v[k], ctx.calc, k);
      }),
    }));
  return { kind: 'table', title: str(block.title), rowLabel: str(block.rowLabel), mode: block.pdf === 'table' ? 'table' : 'cards', columns, rows };
}

function pullBlock(block, ctx) {
  const rows = (block.items || []).map((it) => {
    const raw = ctx.pulled[`${block.id || 'pull'}.${it.id}`];
    return { label: str(it.label), value: str(raw), ref: str(it.ref) };
  });
  return { kind: 'pull', title: str(block.title), rows };
}

// reference blocks and static notes are left out; a note with the tone 'rule' is kept
function buildBlock(block, ctx) {
  if (!block) return null;
  if (block.type === 'fields') return kvBlock(block, ctx);
  if (block.type === 'table') return tableBlock(block, ctx);
  if (block.type === 'pull') return pullBlock(block, ctx);
  if (block.type === 'note' && block.tone === 'rule') return { kind: 'note', text: str(block.text) };
  return null;
}

// ---------------------------------------------------------------- steps, results, sign-off
function buildStep(step, i, ctx) {
  return {
    id: str(step.id) || `s${i + 1}`,
    n: i + 1,
    letter: str(step.letter),
    title: str(step.title),
    minutes: step.minutes ? `${step.minutes} min` : '',
    hint: str(step.hint),
    blocks: (step.blocks || []).map((b) => buildBlock(b, ctx)).filter(Boolean),
  };
}

function buildResults(def, state) {
  const results = (state && state.d && state.d.results) || {};
  return (def.results || []).map((r) => ({ id: r.id, label: str(r.label), value: str(results[r.id]), rule: str(r.rule) }));
}

function buildSignoff(def, v) {
  const so = def.signoff;
  if (!so) return { mode: 'none', sceptic: '', questions: [], entry: [] };
  const mode = so.mode || 'ledger';
  const questions = (def.signoffQuestions || []).map(str).filter(Boolean);
  const objection = str(v['so.objection']);
  const entry = [];
  if (mode !== 'questions') {
    const first = mode === 'panel' ? 'Reviewer role' : 'Sceptic who reviewed it';
    entry.push({ label: first, value: str(v['so.reviewer']) || (objection ? str(def.sceptic) : '') });
    entry.push({ label: 'Objection, in their words', value: objection });
    entry.push({ label: 'Evidence that answers it, and the cell it sits in', value: str(v['so.evidence']) });
    entry.push({ label: 'Follow-up owner (a role)', value: str(v['so.owner']) });
    if (mode === 'ledger') {
      entry.push({ label: 'Status', value: str(v['so.status']) || (objection ? 'Open' : '') });
      entry.push({ label: 'Star (raise first)', value: str(v['so.star']) === 'Yes' ? 'Yes' : '' });
    }
  }
  return { mode, sceptic: str(def.sceptic), questions, entry };
}

// ---------------------------------------------------------------- canvases
function buildCanvas(def, state, up) {
  const s = state || {};
  const v = s.v || {};
  const d = s.d || {};
  const ctx = { v, up, calc: d.calc || {}, pulled: s.pulled || {} };
  const status = s.status === 'Confirmed' || s.status === 'In progress' ? s.status : 'Not started';
  return {
    no: def.no,
    title: str(def.title),
    question: str(def.question),
    arc: str(def.arc),
    family: ARC_FAMILY[def.arc] || 'cyan',
    module: def.module === undefined ? '' : String(def.module),
    day: def.day === undefined ? '' : String(def.day),
    status,
    stale: !!s.stale,
    confirmedOn: status === 'Confirmed' ? formatDate(s.confirmedOn || v._confirmedOn) : '',
    completion: Number.isFinite(s.completion) ? Math.max(0, Math.min(100, s.completion)) : 0,
    results: buildResults(def, s),
    steps: (def.steps || []).map((st, i) => buildStep(st, i, ctx)),
    signoff: buildSignoff(def, v),
  };
}

function buildCaseSummary(states) {
  const f = (states[0] && states[0].d && states[0].d.facts) || {};
  const kpis = [str(f.kpi1), str(f.kpi2)].filter(Boolean).join('; ');
  return {
    journey: str(f.journey),
    function: str(f.function),
    segment: str(f.segment),
    sponsor: str(f.sponsor),
    kpis,
    channels: str(f.channels),
    volume: str(f.volume),
    volumeTag: str(f.volumeTag),
    idea: str(f.idea),
    ideaStage: str(f.ideaStage),
  };
}

// ---------------------------------------------------------------- appendix
const GAP_RANK = { G: 0, E: 1 };

function buildAppendix(agg) {
  const a = agg || {};
  const gaps = (a.gaps || [])
    .filter((g) => g && (g.tag === 'G' || g.tag === 'E'))
    .map((g, i) => ({ tag: g.tag, item: str(g.item), where: str(g.where), _i: i }))
    .sort((x, y) => GAP_RANK[x.tag] - GAP_RANK[y.tag] || x._i - y._i)
    .map(({ _i, ...g }) => g);
  const ledger = (a.ledger || []).map((r) => ({
    where: str(r.where),
    sceptic: str(r.sceptic),
    objection: str(r.objection),
    evidence: str(r.evidence),
    owner: str(r.owner),
    status: str(r.status) || 'Open',
    star: r.star ? 'Yes' : '',
  }));
  return { gaps, ledger };
}

// ---------------------------------------------------------------- the model
// { title, subtitle, learner, generatedOn, caseSummary,
//   index:    [{ no, title, question, status, completion, stale, confirmedOn }],
//   canvases: [{ no, title, question, arc, family, module, day, status, stale, confirmedOn, completion, results, steps, signoff }],
//   appendix: { gaps: [{ tag, item, where }], ledger: [{ where, sceptic, objection, evidence, owner, status, star }] } }
// Blocks: { kind: 'kv', rows: [{ label, value, tag, calc }] }
//         { kind: 'table', title, rowLabel, mode, columns: [{ label, w }], rows: [{ label, sub, cells: [{ value, tag, calc }] }] }
//         { kind: 'pull', title, rows: [{ label, value, ref }] }   { kind: 'note', text }
export function buildDoc({ states, agg, learner, generatedOn } = {}) {
  const st = states || {};
  const aggSafe = { gaps: [], ledger: [], ledgerTop: [], ...(agg || {}) };
  const up = { ...st, agg: aggSafe };
  const nos = Object.keys(st)
    .map(Number)
    .filter((n) => Number.isFinite(n) && st[n] && st[n].def)
    .sort((a, b) => a - b);
  const canvases = nos.map((n) => buildCanvas(st[n].def, st[n], up));
  const who = learner || {};
  return {
    title: 'AICX Canvases',
    subtitle: 'AI-Powered Customer Experience (AICX)',
    learner: { name: str(who.name), email: str(who.email), organisation: str(who.organisation) },
    generatedOn: str(generatedOn),
    caseSummary: buildCaseSummary(st),
    index: canvases.map((c) => ({
      no: c.no,
      title: c.title,
      question: c.question,
      status: c.status,
      completion: `${c.completion}%`,
      stale: c.stale,
      confirmedOn: c.confirmedOn,
    })),
    canvases,
    appendix: buildAppendix(aggSafe),
  };
}
